/**
 * One vocabulary for enrollment, session and progress status — UC-P08's
 * requirement that the parent and the learner read the same state with the
 * same meaning (PRT/STU shared meaning).
 *
 * Every family and teacher screen that names a status takes its words from
 * here, so "Completada" cannot mean one thing on the dashboard and another on
 * the progress screen. Copy is plain Spanish and says "profesor".
 *
 * Constants live here rather than in the API modules: tests replace those
 * modules wholesale, and a screen must not lose its words when they do.
 */

import type {
  EffectiveLearningProfile,
  FeedbackAudience,
  LearningEvidenceStatus,
  LearningProfile,
  ObservationState,
  ProgressMode,
  ProgressSnapshot,
} from '../api/learning';
import type { SessionState } from '../api/cycle-progress';
import type { EnrollmentState } from '../api/enrollments';
import type { DisplayTimeZone } from '../api/learner';
import type { CalendarEventState } from '../api/schedule';
import { formatShortDateTime } from '../time/zonedTime';
import { joinStateAt } from '../learner/joinWindow';

export { ENROLLMENT_STATE_LABEL } from '../components/enrollmentRequest';

/**
 * A stored profile, or `LEGACY_UNASSIGNED` when there is none. An absent
 * profile is not LEP_2 (MC-01 v1-7 §4, §8): nobody decided it, and the screen
 * must not decide it either.
 */
export const profileOf = (value?: string | null): EffectiveLearningProfile =>
  value === 'LEP_0' || value === 'LEP_1' || value === 'LEP_2' || value === 'LEP_3'
    ? value
    : 'LEGACY_UNASSIGNED';

/** Rubric, milestones and final project exist only for the deferred LEP_2 / LEP_3. */
export const profileHasMilestones = (p: EffectiveLearningProfile): boolean =>
  p === 'LEP_2' || p === 'LEP_3';

/**
 * What the closing milestone is called (DEC-S3-T10-02): a final project only
 * where the course has one; the close of the cycle everywhere else, and
 * wherever the profile is not at hand. Backend twin: `closingMilestoneLabel`.
 */
export const closingLabel = (p?: EffectiveLearningProfile): string =>
  p && profileHasMilestones(p) ? 'Proyecto final' : 'Cierre del ciclo';

/** The same words, for inside a sentence: "el cierre del ciclo". */
export const closingLabelInline = (p?: EffectiveLearningProfile): string =>
  `el ${closingLabel(p).toLowerCase()}`;

/** Whether a teacher may write a rubric at all: never on LEP_0/1 (MC07-R02). */
export const profileAdmitsRubric = (p: EffectiveLearningProfile): boolean =>
  p !== 'LEP_0' && p !== 'LEP_1';

/** The profiles a teacher may choose for a new or edited course (MC01-R13). */
export const MVP_PROFILES: readonly LearningProfile[] = ['LEP_0', 'LEP_1'];

/**
 * The teacher's choice when authoring a course — LEP_0 and LEP_1 only. The
 * words follow the LEM/LEP reference guide (v1.0, §2 and §4): Experiencia and
 * Práctica guiada, explained in plain language.
 */
export const LEARNING_PROFILE_OPTIONS: ReadonlyArray<{
  value: LearningProfile;
  label: string;
  help: string;
}> = [
  {
    value: 'LEP_0',
    label: 'Experiencia',
    help:
      'Para explorar, participar y descubrir. La familia ve las clases a las que asistió, ' +
      'los temas y las próximas sesiones. Sin notas, rúbricas ni proyecto final.',
  },
  {
    value: 'LEP_1',
    label: 'Práctica guiada',
    help:
      'Para practicar una habilidad con orientación. Después de cada clase describes con una ' +
      'palabra cómo viste al alumno y dejas un comentario breve. El avance es cualitativo: ' +
      'sin nota ni rúbrica.',
  },
];

/** Said once under the choice: what neither option does. */
export const LEARNING_PROFILE_NOTE =
  'En los dos, cada clase se paga cuando está impartida y con su evidencia; nunca depende de ' +
  'notas ni de trabajos del alumno. Puedes cerrar el curso con una actividad en clase, sin ' +
  'entrega obligatoria.';

/** How a family reads the profile in one phrase. */
export const LEARNING_PROFILE_LABEL: Record<EffectiveLearningProfile, string> = {
  LEP_0: 'Experiencia',
  LEP_1: 'Práctica guiada',
  LEP_2: 'Aprendizaje estructurado',
  LEP_3: 'Ruta de dominio',
  LEGACY_UNASSIGNED: 'Seguimiento de clases',
};

/**
 * The snapshot's projection mode — what the server sent, or, from a server
 * that predates MC-01 v1-7, what the profile implies.
 */
export function progressModeOf(s: Pick<ProgressSnapshot, 'progressMode' | 'learningProfile'>) {
  if (s.progressMode) return s.progressMode;
  const p = profileOf(s.learningProfile);
  const byProfile: Record<EffectiveLearningProfile, ProgressMode> = {
    LEP_0: 'HISTORY_ONLY',
    LEP_1: 'SIMPLE_QUALITATIVE',
    LEP_2: 'STRUCTURED_OUTCOME',
    LEP_3: 'MASTERY_PROGRESSION',
    LEGACY_UNASSIGNED: 'SERVICE_ONLY',
  };
  return byProfile[p];
}

/** LEP_1's working vocabulary. Words, not a grade. */
export const OBSERVATION_LABEL: Record<ObservationState, string> = {
  STARTING: 'Empezando',
  DEVELOPING: 'En desarrollo',
  CONSISTENT: 'Constante',
  EXTENDING: 'Va más allá',
};

export const OBSERVATION_HELP: Record<ObservationState, string> = {
  STARTING: 'Está conociendo el tema y necesita mucho apoyo.',
  DEVELOPING: 'Avanza con apoyo y ya resuelve parte por su cuenta.',
  CONSISTENT: 'Lo hace por su cuenta de forma estable.',
  EXTENDING: 'Lo aplica en situaciones nuevas o ayuda a otros.',
};

export const OBSERVATION_ORDER: readonly ObservationState[] = [
  'STARTING',
  'DEVELOPING',
  'CONSISTENT',
  'EXTENDING',
];

/** Shown wherever an observation is: this is not an approved academic scale. */
export const WORKING_VOCABULARY_NOTE =
  'Vocabulario de trabajo del profesor para describir una sesión. No es una calificación ni una escala académica.';

export const FEEDBACK_AUDIENCE_LABEL: Record<FeedbackAudience, string> = {
  LEARNER_SHARED: 'Para el alumno y su familia',
  PARENT_PRIVATE: 'Solo para la familia',
  TEACHER_PRIVATE: 'Solo para ti (privado)',
};

export const FEEDBACK_AUDIENCE_HELP: Record<FeedbackAudience, string> = {
  LEARNER_SHARED: 'Lo ven el alumno y su madre, padre o tutor.',
  PARENT_PRIVATE: 'Lo ve solo el adulto responsable. Nunca aparece en la pantalla del alumno.',
  TEACHER_PRIVATE: 'Nadie de la familia lo ve.',
};

/** A session as both the family and the teacher read it. */
export const SESSION_STATE_LABEL: Record<SessionState, string> = {
  Scheduled: 'Programada',
  InProgress: 'En curso',
  AwaitingValidation: 'Revisando la evidencia',
  Completed: 'Completada',
  Disputed: 'En revisión por un reclamo',
  NotDelivered: 'No impartida: el profesor no se presentó y se devolvió el pago',
};

/** A milestone's money state, for the teacher and support. */
export const MILESTONE_STATUS_LABEL: Record<string, string> = {
  pending: 'Pendiente',
  in_progress: 'En curso',
  completed: 'Completado',
  released: 'Pago liberado',
  disputed: 'En disputa',
};

/** LearningGateFacts' evidence status. Never "liberado": that is a different fact. */
export const EVIDENCE_STATUS_LABEL: Record<LearningEvidenceStatus, string> = {
  N_A: 'No aplica a este curso',
  PENDING: 'Pendiente',
  SATISFIED: 'Completa',
  NOT_SATISFIED: 'No se completó',
};

/** "pendiente" — never 0 % — when the server says there is not enough history yet. */
export const NOT_ENOUGH_HISTORY_COPY =
  'Pendiente: todavía no hay suficientes clases registradas para mostrar el avance.';

/**
 * The progress line a family reads, from the snapshot alone.
 * Returns null while there is not enough history — the caller shows "pendiente".
 */
export function progressHeadline(s: ProgressSnapshot): string | null {
  if (s.dataSufficiency === 'NOT_ENOUGH_HISTORY') return null;
  const mode = progressModeOf(s);
  const total = s.sessionsTotal ? ` de ${s.sessionsTotal}` : '';
  if (mode === 'SERVICE_ONLY') {
    // The service, said as the service: classes given, not progress made.
    const n = s.sessionsDelivered ?? 0;
    return `${n}${total} ${n === 1 && !s.sessionsTotal ? 'clase impartida' : 'clases impartidas'}`;
  }
  if ((mode === 'STRUCTURED_OUTCOME' || mode === 'MASTERY_PROGRESSION') && s.milestones) {
    return `${s.milestones.completed} de ${s.milestones.total} hitos completados`;
  }
  const n = s.sessionsAttended ?? 0;
  return `${n}${total} ${n === 1 && !s.sessionsTotal ? 'clase asistida' : 'clases asistidas'}`;
}

/**
 * STU-13-FR-04 — the classes that took place without the learner, said
 * neutrally. They are not in the headline: under CHG-002 a missed class
 * completes for payment, and it is still not progress. Null when none.
 */
export function missedClassesLine(s: ProgressSnapshot): string | null {
  const n = s.sessionsMissed ?? 0;
  if (n <= 0) return null;
  return n === 1
    ? '1 clase sin asistir: no cuenta como avance.'
    : `${n} clases sin asistir: no cuentan como avance.`;
}

export type JoinState = 'OPEN' | 'NOT_YET' | 'CLOSED' | 'UNAVAILABLE';

/**
 * Whether "Entrar a la clase" is offered right now (PRT-09-FR-07/08).
 * The window itself is the server's; the comparison is the learner door's own
 * (`learner/joinWindow.ts`), so the parent and the child cannot disagree on
 * when the door is open. Only an Active enrollment is ever joinable.
 */
export function joinState(s: ProgressSnapshot, now: Date): JoinState {
  if (s.enrollmentState && s.enrollmentState !== 'Active') return 'UNAVAILABLE';
  if (!s.joinWindow || !s.nextSession) return 'UNAVAILABLE';
  switch (joinStateAt(s.joinWindow, now)) {
    case 'BEFORE_WINDOW':
      return 'NOT_YET';
    case 'ENDED':
      return 'CLOSED';
    default:
      return 'OPEN';
  }
}

/** A session instant on a family card, in the server-chosen zone (UC-P08 BR-P08-04). */
export const fmtSessionDate = (iso: string | undefined, tz: DisplayTimeZone): string =>
  formatShortDateTime(iso, tz);

/**
 * UC-P08 "Mi aprendizaje" — which enrollments are active learning. Running
 * classes, and a request the family has authorized that waits for its
 * teacher. Not a checkout that never completed (commercial, PRT-D06), not a
 * finished one (past classes, UC-P10), not a cancelled, declined or expired
 * one. One list, so the count and the cards cannot disagree (PRT-09-FR-02).
 */
export const ACTIVE_LEARNING_STATES: readonly EnrollmentState[] = [
  'Active',
  'PendingTeacherAcceptance',
];

export const isActiveLearning = (state: EnrollmentState): boolean =>
  ACTIVE_LEARNING_STATES.includes(state);

/** A calendar event's state as the family reads it (PRT-14-FR-07). */
export const CALENDAR_EVENT_STATE_LABEL: Record<CalendarEventState, string> = {
  Confirmed: 'Confirmada',
  Rescheduled: 'Cambio de horario pendiente',
  Cancelled: 'Cancelada',
  Completed: 'Realizada',
  NoShow: 'Sin asistencia',
};
